import { useEffect, useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import { EmptyState, SkeletonList } from "@/components/base/StateViews";
import CategoryHeader from "@/pages/category/components/CategoryHeader";
import FilterBar, { type FilterGroup } from "@/pages/category/components/FilterBar";
import TvCard from "@/pages/category/tv/components/TvCard";
import { collectGenres, collectRegions, collectYears, filterWorks } from "@/utils/data";
import { useSeo } from "@/hooks/useSeo";
import type { Work } from "@/types";

const PAGE_SIZE = 6;
const sortOptions = ["最新", "热门"];
const statusOptions = ["全部", "连载", "完结"];

export default function TvCategory() {
  useSeo({
    title: "电视剧分类 - 亚洲天堂",
    description:
      "亚洲天堂电视剧分类频道，汇集古装、都市、科幻、悬疑等热播剧集，支持按类型、地区、年份与更新状态筛选，掌握最新剧集动态。",
    keywords: "电视剧分类,电视剧动态,热播剧集,亚洲天堂电视剧,剧集推荐",
  });

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [airStatus, setAirStatus] = useState("全部");
  const [sort, setSort] = useState("最新");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const genres = useMemo(() => collectGenres("tv"), []);
  const regions = useMemo(() => collectRegions("tv"), []);
  const years = useMemo(() => collectYears("tv"), []);

  const list: Work[] = useMemo(
    () =>
      filterWorks({
        type: "tv",
        genre,
        region,
        year,
        airStatus,
        sort: sort === "热门" ? "hot" : "latest",
      }),
    [genre, region, year, airStatus, sort],
  );

  useEffect(() => {
    setPage(1);
  }, [genre, region, year, airStatus, sort]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [genre, region, year, airStatus, sort]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageItems = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const groups: FilterGroup[] = [
    { key: "genre", label: "类型", options: genres, value: genre },
    { key: "region", label: "地区", options: regions, value: region },
    { key: "year", label: "年份", options: years, value: year },
    { key: "airStatus", label: "状态", options: statusOptions, value: airStatus },
    { key: "sort", label: "排序", options: sortOptions, value: sort },
  ];

  const handleChange = (key: string, value: string) => {
    if (key === "genre") setGenre(value);
    if (key === "region") setRegion(value);
    if (key === "year") setYear(value);
    if (key === "airStatus") setAirStatus(value);
    if (key === "sort") setSort(value);
  };

  const handleReset = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setAirStatus("全部");
    setSort("最新");
  };

  return (
    <div className="animate-fade-in">
      <CategoryHeader
        title="电视剧分类"
        description="追踪热播剧集的更新动态，按类型、地区与连载状态筛选，不错过任何一集精彩。"
        icon="ri-tv-2-line"
        breadcrumb={<Breadcrumb items={[{ label: "电视剧" }]} />}
      />

      <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
        <FilterBar groups={groups} onChange={handleChange} onReset={handleReset} />

        {loading ? (
          <SkeletonList count={5} />
        ) : list.length === 0 ? (
          <EmptyState
            title="暂无符合条件的电视剧"
            description="换一组筛选条件试试，或者重置查看全部剧集。"
            actionLabel="重置筛选"
            onAction={handleReset}
            icon="ri-tv-2-line"
          />
        ) : (
          <>
            <div className="mb-4 text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{list.length}</strong> 部电视剧
            </div>
            <div className="flex flex-col gap-4">
              {pageItems.map((work) => (
                <TvCard key={work.id} work={work} />
              ))}
            </div>
            <div className="mt-8">
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}